import React, { useEffect, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";

import MainButton from "../components/MainButton";
import FriendsGroupIcon from "../assets/icons/friends-group-icon.svg";
import SellTicketIcon from "../assets/icons/ticket-pink-icon.svg";
import RefsGroupIcon from "../assets/icons/ref-group.svg";

import {
  getLevelK,
  getLevelPriceNano,
  getPlayerBasicInfo,
  getPlayerReferrer,
  PlayerBasicInfoDTO,
} from "../blockchain/lotteryClient";
import { shortenAddress } from "../utils/address";
import { formatGram } from "../utils/gram";
import {
  REFERRAL_MILESTONE_FRIENDS,
  REFERRAL_WIN_PERCENTS,
  referralMilestoneBonusNano,
  soldBonusPercent,
} from "../constants/lotteryRules";

function Friends() {
  const wallet = useTonWallet();
  const address = wallet?.account.address;

  const [player, setPlayer] = useState<PlayerBasicInfoDTO | null>(null);
  const [referrer, setReferrer] = useState<string | null>(null);
  const [basePriceNano, setBasePriceNano] = useState<bigint | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [regularPrice, regularK] = await Promise.all([getLevelPriceNano(0), getLevelK(0)]);
        if (!cancelled && regularK > 0) setBasePriceNano(regularPrice / BigInt(regularK));
      } catch (error) {
        console.error("Failed to load base ticket price:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!address) {
      setPlayer(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const info = await getPlayerBasicInfo(address);
        if (!cancelled) setPlayer(info);
      } catch {
        if (!cancelled) setPlayer(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [address]);

  useEffect(() => {
    if (!address) {
      setReferrer(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const ref = await getPlayerReferrer(address);

        if (!cancelled) {
          setReferrer(ref); // string | null
        }
      } catch {
        if (!cancelled) {
          setReferrer(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [address]);


  const shortAddress = shortenAddress(address);

  const myFriends = player?.referredCount ?? 0;
  const soldTickets = player?.ticketsSoldByReferrals ?? 0;

  const referralIncome = Number(player?.referralBonus ?? 0n) / 1e9;
  const soldIncome = Number(player?.soldBonus ?? 0n) / 1e9;
  const totalIncome = referralIncome + soldIncome;

  const milestoneBonus = basePriceNano !== null
    ? `${formatGram(referralMilestoneBonusNano(basePriceNano, player?.userLevel ?? 1))} GRAM`
    : "-- GRAM";
  const [level1Percent, level2Percent, level3Percent] = REFERRAL_WIN_PERCENTS;

  const handleOpenShareModal = () => {
    if (!address) return;

    const link = `t.me/cryptolottery777_bot/CryptoLottery777/?ref=${address}`;
    const text = `Привет! Присоединяйся по моей реферальной ссылке 👇`;

    const telegramLink =
      `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;

    window.open(telegramLink, "_blank");
  };

  return (
    <div className="friends">
      <div className="friends_header">
        <span className="friends_header_title">Реферальная программа</span>
        <div className="friends_header__content">
          Получай 3–10% с каждой покупки друзей по твоей ссылке и {level1Percent}% / {level2Percent}% / {level3Percent}% с их выигрышей по трём уровням. Смарт-контракт выплачивает бонусы сразу.
        </div>
      </div>

      <div className="friends_identifier">
        <div className="friends_identifier__title">Ваш идентификатор</div>
        <div className="friends_identifier__content">
          <span>{shortAddress}</span>
        </div>
      </div>

      <MainButton
        text="Поделиться моей ссылкой"
        func={handleOpenShareModal}
      />

      <div className="friends_friends-group">
        <div className="friends_friends-group__title">Друзья</div>

        <div className="friends_friends-group__item">
          <div className="friends_friends-group__item___info">
            <img src={FriendsGroupIcon} alt="" width={32} height={32} />
            <span>Мои друзья</span>
          </div>
          <div className="friends_friends-group__item___count">
            {myFriends}
          </div>
        </div>
      </div>

      <div className="friends_friends-group">
        <div className="friends_friends-group__title flex-style">
          <span>Заработок от друзей</span>
          <span>{totalIncome.toFixed(2)} GRAM</span>
        </div>

        <div className="friends_friends-group__item">
          <div className="friends_friends-group__item___info">
            <img src={RefsGroupIcon} alt="" width={32} height={32} />
            <div>
              <span>Привлечь рефералов</span>
              <div className="friends-card-subtitle">{milestoneBonus} за каждые {REFERRAL_MILESTONE_FRIENDS} друзей</div>
            </div>
          </div>
          <div className="friends_friends-group__item___count">
            {myFriends}
          </div>
        </div>

        <div className="friends_friends-group__item">
          <div className="friends_friends-group__item___info">
            <img src={SellTicketIcon} alt="" width={32} height={32} />
            <div>
              <span>Продать билетов</span>
              <div className="friends-card-subtitle">{soldBonusPercent(soldTickets)}% от суммы покупки</div>
            </div>
          </div>
          <div className="friends_friends-group__item___count">
            {soldTickets}
          </div>
        </div>
      </div>

      <div className="friends_identifier">
        <div className="friends_identifier__title">Ваш пригласитель</div>
        <div className="friends_identifier__content">
          <span title={referrer ?? undefined}>{shortenAddress(referrer)}</span>
        </div>
      </div>
    </div>
  );
}

export default Friends;
